import React, { useState, useEffect } from 'react';
import { AlertTriangle, X, CheckCircle, AlertCircle, ArrowRight, Users, Merge } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * DuplicateWarningPanel - Real-time duplicate detection for forms
 * 
 * Usage:
 * <DuplicateWarningPanel
 *   entityType="contractor"
 *   checkData={{ company_name: "...", email: "...", phone: "..." }}
 *   excludeId={existingContractorId} // For updates
 *   onUseExisting={(duplicate) => { ... }}
 *   onOverride={() => setOverrideWarning(true)}
 * />
 */
export default function DuplicateWarningPanel({
  entityType = 'contractor', // 'contractor' | 'client' | 'park'
  checkData = {},
  excludeId = null,
  onUseExisting = null,
  onOverride = null,
  className = ''
}) {
  const [checking, setChecking] = useState(false);
  const [result, setResult] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [debounceTimer, setDebounceTimer] = useState(null);

  // Debounce check to avoid too many API calls
  useEffect(() => {
    if (debounceTimer) {
      clearTimeout(debounceTimer);
    }

    const hasData = Object.values(checkData).some(v => v && v.trim && v.trim().length > 2);
    
    if (!hasData) {
      setResult(null);
      return;
    }

    const timer = setTimeout(() => {
      checkForDuplicates();
    }, 500);

    setDebounceTimer(timer);

    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [JSON.stringify(checkData), excludeId]);

  const checkForDuplicates = async () => {
    setChecking(true);
    try {
      const token = localStorage.getItem('token');
      const payload = {
        ...checkData,
        exclude_id: excludeId
      };

      const res = await axios.post(
        `${API}/api/data-audit/${entityType}s/check-duplicate`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setResult(res.data);
      if (res.data.has_warning || res.data.has_block) {
        setExpanded(true);
      }
    } catch (err) {
      console.error('Duplicate check failed:', err);
      setResult(null);
    } finally {
      setChecking(false);
    }
  };

  if (!result || (!result.has_warning && !result.has_block)) {
    return null;
  }

  const isBlock = result.has_block;
  const duplicates = result.duplicates || [];

  return (
    <div className={`rounded-xl border ${isBlock ? 'border-red-300 bg-red-50' : 'border-amber-300 bg-amber-50'} overflow-hidden ${className}`}>
      {/* Header */}
      <div 
        className={`flex items-center justify-between px-4 py-3 cursor-pointer ${isBlock ? 'bg-red-100' : 'bg-amber-100'}`}
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3">
          {isBlock ? (
            <AlertCircle className="w-5 h-5 text-red-600" />
          ) : (
            <AlertTriangle className="w-5 h-5 text-amber-600" />
          )}
          <div>
            <p className={`font-semibold ${isBlock ? 'text-red-800' : 'text-amber-800'}`}>
              {isBlock ? 'Duplicate Detected' : 'Possible Duplicate Found'}
            </p>
            <p className={`text-sm ${isBlock ? 'text-red-600' : 'text-amber-600'}`}>
              {duplicates.length} matching record{duplicates.length !== 1 ? 's' : ''} found
              {result.highest_score && ` • ${result.highest_score}% match`}
            </p>
          </div>
        </div>
        <button className={`p-1 rounded ${isBlock ? 'hover:bg-red-200' : 'hover:bg-amber-200'}`}>
          {expanded ? <X className="w-4 h-4" /> : <ArrowRight className="w-4 h-4" />}
        </button>
      </div>

      {/* Expanded Content */}
      {expanded && (
        <div className="p-4 space-y-4">
          {duplicates.map((dupe, idx) => (
            <div 
              key={dupe.id || idx}
              className="bg-white rounded-lg border border-slate-200 p-4"
            >
              <div className="flex items-start justify-between gap-4">
                {/* Duplicate Info */}
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <Users className="w-4 h-4 text-slate-400" />
                    <span className="font-medium text-slate-800">
                      {entityType === 'contractor' && (dupe.company_name || 'Unnamed Contractor')}
                      {entityType === 'client' && (dupe.name || 'Unnamed Client')}
                      {entityType === 'park' && (dupe.name || 'Unnamed Park')}
                    </span>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                      dupe.match_score >= 90 ? 'bg-red-100 text-red-700' :
                      dupe.match_score >= 70 ? 'bg-amber-100 text-amber-700' :
                      'bg-slate-100 text-slate-700'
                    }`}>
                      {dupe.match_score}% match
                    </span>
                  </div>

                  <div className="grid grid-cols-2 gap-2 text-sm">
                    {dupe.email && (
                      <div className="flex items-center gap-1">
                        <span className="text-slate-500">Email:</span>
                        <span className={`${dupe.match_reasons?.includes('email_exact') ? 'text-red-600 font-medium' : 'text-slate-700'}`}>
                          {dupe.email}
                        </span>
                      </div>
                    )}
                    {dupe.phone && (
                      <div className="flex items-center gap-1">
                        <span className="text-slate-500">Phone:</span>
                        <span className={`${dupe.match_reasons?.includes('phone_exact') ? 'text-red-600 font-medium' : 'text-slate-700'}`}>
                          {dupe.phone}
                        </span>
                      </div>
                    )}
                    {dupe.postcode && (
                      <div className="flex items-center gap-1">
                        <span className="text-slate-500">Postcode:</span>
                        <span className={`${dupe.match_reasons?.includes('postcode_exact') ? 'text-red-600 font-medium' : 'text-slate-700'}`}>
                          {dupe.postcode}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Match Reasons */}
                  {dupe.match_reasons && dupe.match_reasons.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {dupe.match_reasons.map((reason, i) => (
                        <span key={i} className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded text-xs">
                          {reason.replace(/_/g, ' ')}
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {/* Action Button */}
                {onUseExisting && (
                  <button
                    onClick={() => onUseExisting(dupe)}
                    className="flex items-center gap-2 px-3 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 transition-colors"
                  >
                    <CheckCircle className="w-4 h-4" />
                    Use This
                  </button>
                )}
              </div>
            </div>
          ))}

          {/* Action Buttons */}
          <div className="flex items-center justify-between pt-2 border-t border-slate-200">
            {isBlock ? (
              <p className="text-sm text-red-600">
                Cannot create duplicate record. Please use an existing record or modify your data.
              </p>
            ) : (
              <>
                <p className="text-sm text-amber-600">
                  Continue anyway if this is intentionally a different record.
                </p>
                {onOverride && (
                  <button
                    onClick={onOverride}
                    className="flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg text-sm font-medium hover:bg-amber-700 transition-colors"
                  >
                    <AlertTriangle className="w-4 h-4" />
                    Create Anyway
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * DuplicateMergeCard - Side-by-side comparison for merge decisions
 */
export function DuplicateMergeCard({
  recordA,
  recordB,
  entityType,
  matchScore,
  matchReasons = [],
  onMerge,
  onDismiss
}) {
  const [loadingRelated, setLoadingRelated] = useState(false);
  const [relatedA, setRelatedA] = useState(null);
  const [relatedB, setRelatedB] = useState(null);
  const [mergeDirection, setMergeDirection] = useState('a_to_b'); // Source -> Target

  useEffect(() => {
    loadRelatedCounts();
  }, [recordA?.id, recordB?.id]);

  const loadRelatedCounts = async () => {
    if (!recordA?.id || !recordB?.id) return;
    
    setLoadingRelated(true);
    try {
      const token = localStorage.getItem('token');
      const [resA, resB] = await Promise.all([
        axios.get(`${API}/api/admin/data-merge/${entityType}s/${recordA.id}/related`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/api/admin/data-merge/${entityType}s/${recordB.id}/related`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setRelatedA(resA.data);
      setRelatedB(resB.data);
    } catch (err) {
      console.error('Failed to load related counts:', err);
    } finally {
      setLoadingRelated(false);
    }
  };

  const handleMerge = () => {
    const sourceId = mergeDirection === 'a_to_b' ? recordA.id : recordB.id;
    const targetId = mergeDirection === 'a_to_b' ? recordB.id : recordA.id;
    onMerge?.(sourceId, targetId);
  };

  const getFieldValue = (record, field) => {
    return record?.[field] || '-';
  };

  const fields = entityType === 'contractor' 
    ? ['company_name', 'email', 'phone', 'trade_type']
    : entityType === 'client'
    ? ['name', 'email', 'phone', 'client_type']
    : ['name', 'postcode', 'address', 'region'];

  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
      {/* Header */}
      <div className="bg-slate-50 px-4 py-3 border-b border-slate-200 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Merge className="w-5 h-5 text-slate-600" />
          <span className="font-semibold text-slate-800">
            Duplicate {entityType.charAt(0).toUpperCase() + entityType.slice(1)}s
          </span>
          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
            matchScore >= 90 ? 'bg-red-100 text-red-700' :
            matchScore >= 70 ? 'bg-amber-100 text-amber-700' :
            'bg-slate-100 text-slate-700'
          }`}>
            {matchScore}% match
          </span>
        </div>
        {onDismiss && (
          <button onClick={onDismiss} className="text-slate-400 hover:text-slate-600">
            <X className="w-5 h-5" />
          </button>
        )}
      </div>

      {/* Side-by-side comparison */}
      <div className="grid grid-cols-2 divide-x divide-slate-200">
        {/* Record A */}
        <div className="p-4">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium text-slate-600">Record A</span>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="radio"
                name="mergeDirection"
                checked={mergeDirection === 'a_to_b'}
                onChange={() => setMergeDirection('a_to_b')}
                className="text-teal-600"
              />
              <span className="text-slate-600">Merge into B</span>
            </label>
          </div>
          <div className="space-y-2">
            {fields.map(field => (
              <div key={field} className="flex justify-between text-sm">
                <span className="text-slate-500 capitalize">{field.replace(/_/g, ' ')}:</span>
                <span className={`text-slate-800 ${matchReasons.includes(`${field}_exact`) ? 'text-red-600 font-medium' : ''}`}>
                  {getFieldValue(recordA, field)}
                </span>
              </div>
            ))}
          </div>
          {relatedA && (
            <div className="mt-3 pt-3 border-t border-slate-100">
              <span className="text-xs text-slate-500">Related Records: {relatedA.total_related}</span>
            </div>
          )}
        </div>

        {/* Record B */}
        <div className="p-4">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium text-slate-600">Record B</span>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="radio"
                name="mergeDirection"
                checked={mergeDirection === 'b_to_a'}
                onChange={() => setMergeDirection('b_to_a')}
                className="text-teal-600"
              />
              <span className="text-slate-600">Merge into A</span>
            </label>
          </div>
          <div className="space-y-2">
            {fields.map(field => (
              <div key={field} className="flex justify-between text-sm">
                <span className="text-slate-500 capitalize">{field.replace(/_/g, ' ')}:</span>
                <span className={`text-slate-800 ${matchReasons.includes(`${field}_exact`) ? 'text-red-600 font-medium' : ''}`}>
                  {getFieldValue(recordB, field)}
                </span>
              </div>
            ))}
          </div>
          {relatedB && (
            <div className="mt-3 pt-3 border-t border-slate-100">
              <span className="text-xs text-slate-500">Related Records: {relatedB.total_related}</span>
            </div>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="bg-slate-50 px-4 py-3 border-t border-slate-200 flex items-center justify-between">
        <div className="text-sm text-slate-600">
          <span className="font-medium">
            {mergeDirection === 'a_to_b' ? 'A' : 'B'}
          </span>
          {' → '}
          <span className="font-medium">
            {mergeDirection === 'a_to_b' ? 'B' : 'A'}
          </span>
          <span className="text-slate-500 ml-2">
            (Source will be archived, references updated to Target)
          </span>
        </div>
        <button
          onClick={handleMerge}
          className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 transition-colors"
        >
          <Merge className="w-4 h-4" />
          Merge Records
        </button>
      </div>
    </div>
  );
}
